import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOffIcon from '@mui/icons-material/PersonOffOutlined';
import { toast } from 'react-toastify';
import {
  PageHeader,
  PageFilters,
  DataTable,
  TablePaginationBar,
  StatusBadge,
  ActionMenu,
  ConfirmDialog,
} from '../../components/ui';
import type { Column, ActionItem } from '../../components/ui';
import { providersApi } from '../../api/providers';
import { PAGE_SIZE, SERVICE_CATEGORIES } from '../../constants';
import { formatDate, paginate, matchesSearch, initials } from '../../utils';
import type { AccountStatus, ServiceProvider } from '../../types';

type ConfirmAction = {
  id: string;
  name: string;
  status: AccountStatus;
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
};

const STATUS_OPTIONS = [
  { label: 'All', value: 'All' },
  { label: 'Active', value: 'Active' },
  { label: 'Suspended', value: 'Suspended' },
  { label: 'Deactivated', value: 'Deactivated' },
];

const VERIFICATION_OPTIONS = [
  { label: 'All', value: 'All' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
];

const CATEGORY_OPTIONS = [
  { label: 'All', value: 'All' },
  ...SERVICE_CATEGORIES.map((c) => ({ label: c, value: c })),
];

const ProvidersPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [status, setStatus] = useState('All');
  const [verification, setVerification] = useState('All');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('applicationDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    providersApi
      .getProviders({ page: 1, limit: 1000 })
      .then((res) => setProviders(res.providers))
      .catch(() => {
        toast.error('Failed to load providers');
        setProviders([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let rows = providers.filter((p) => {
      const q = search.trim();
      const matchesQ =
        !q || matchesSearch(p.name, q) || matchesSearch(p.email, q);
      const matchesCategory =
        category === 'All' || p.primaryCategory === category;
      const matchesStatus = status === 'All' || p.status === status;
      const matchesVerification =
        verification === 'All' || p.verificationStatus === verification;
      return matchesQ && matchesCategory && matchesStatus && matchesVerification;
    });

    rows = [...rows].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortKey === 'applicationDate') {
        cmp =
          new Date(a.applicationDate).getTime() -
          new Date(b.applicationDate).getTime();
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return rows;
  }, [providers, search, category, status, verification, sortKey, sortDir]);

  useEffect(() => {
    setPage(1);
  }, [search, category, status, verification]);

  const pageRows = useMemo(
    () => paginate(filtered, page, PAGE_SIZE),
    [filtered, page]
  );

  const handleSort = (columnId: string) => {
    if (sortKey === columnId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(columnId);
      setSortDir('asc');
    }
  };

  const openStatusConfirm = (
    provider: ServiceProvider,
    nextStatus: AccountStatus
  ) => {
    const map: Record<
      AccountStatus,
      Omit<ConfirmAction, 'id' | 'name' | 'status'>
    > = {
      Suspended: {
        title: 'Suspend provider',
        message: `Suspend ${provider.name}? They will temporarily lose access.`,
        confirmLabel: 'Suspend',
        danger: true,
      },
      Active: {
        title: 'Reactivate provider',
        message: `Reactivate ${provider.name}? They will regain full access.`,
        confirmLabel: 'Reactivate',
      },
      Deactivated: {
        title: 'Deactivate provider',
        message: `Deactivate ${provider.name}? This disables the account.`,
        confirmLabel: 'Deactivate',
        danger: true,
      },
      Inactive: {
        title: 'Update status',
        message: `Set ${provider.name} to Inactive?`,
        confirmLabel: 'Confirm',
      },
    };
    const cfg = map[nextStatus];
    setConfirm({
      id: provider.id,
      name: provider.name,
      status: nextStatus,
      ...cfg,
    });
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      const shouldBlock = confirm.status === 'Suspended' || confirm.status === 'Deactivated';
      await providersApi.toggleBlockProvider(confirm.id, shouldBlock);
      toast.success(`Provider ${confirm.name} is now ${confirm.status}`);
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update provider status');
    } finally {
      setConfirmLoading(false);
    }
  };

  const getActions = (row: ServiceProvider): ActionItem[] => [
    {
      label: 'View',
      icon: <VisibilityIcon fontSize="small" />,
      onClick: () => navigate(`/providers/${row.id}`),
    },
    {
      label: 'Suspend',
      icon: <BlockIcon fontSize="small" />,
      onClick: () => openStatusConfirm(row, 'Suspended'),
      disabled: row.status === 'Suspended' || row.status === 'Deactivated',
      danger: true,
    },
    {
      label: 'Reactivate',
      icon: <CheckCircleIcon fontSize="small" />,
      onClick: () => openStatusConfirm(row, 'Active'),
      disabled: row.status === 'Active',
    },
    {
      label: 'Deactivate',
      icon: <PersonOffIcon fontSize="small" />,
      onClick: () => openStatusConfirm(row, 'Deactivated'),
      disabled: row.status === 'Deactivated',
      danger: true,
    },
  ];

  const columns: Column<ServiceProvider>[] = [
    {
      id: 'id',
      label: 'ID',
      minWidth: 100,
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.id}
        </Typography>
      ),
    },
    {
      id: 'name',
      label: 'Name',
      minWidth: 180,
      sortable: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar
            src={row.avatar}
            sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'secondary.main' }}
          >
            {initials(row.name)}
          </Avatar>
          <Typography variant="body2" fontWeight={600}>
            {row.name}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 180,
      render: (row) => row.email,
    },
    {
      id: 'phone',
      label: 'Phone',
      minWidth: 140,
      render: (row) => row.phone,
    },
    {
      id: 'primaryCategory',
      label: 'Primary Category',
      minWidth: 140,
      render: (row) => row.primaryCategory,
    },
    {
      id: 'experience',
      label: 'Experience',
      minWidth: 100,
      render: (row) => `${row.experienceYears} yrs`,
    },
    {
      id: 'applicationDate',
      label: 'Application Date',
      minWidth: 140,
      sortable: true,
      render: (row) => formatDate(row.applicationDate),
    },
    {
      id: 'verificationStatus',
      label: 'Verification Status',
      minWidth: 140,
      render: (row) => <StatusBadge status={row.verificationStatus} />,
    },
    {
      id: 'status',
      label: 'Account Status',
      minWidth: 120,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      minWidth: 80,
      render: (row) => <ActionMenu actions={getActions(row)} />,
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Service Providers"
        subtitle="Manage provider applications and accounts"
      />

      <PageFilters
        search={search}
        searchPlaceholder="Search by name or email…"
        onSearchChange={setSearch}
        filters={[
          {
            id: 'category',
            label: 'Category',
            value: category,
            options: CATEGORY_OPTIONS,
            onChange: setCategory,
          },
          {
            id: 'status',
            label: 'Account Status',
            value: status,
            options: STATUS_OPTIONS,
            onChange: setStatus,
          },
          {
            id: 'verification',
            label: 'Verification',
            value: verification,
            options: VERIFICATION_OPTIONS,
            onChange: setVerification,
          },
        ]}
      />

      <DataTable
        columns={columns}
        rows={pageRows}
        loading={loading}
        emptyTitle="No providers found"
        emptyDescription="Try a different search or filter combination."
        onSort={handleSort}
        sortKey={sortKey}
        sortDir={sortDir}
        getRowId={(row) => row.id}
      />

      {!loading && filtered.length > 0 && (
        <TablePaginationBar
          page={page}
          pageSize={PAGE_SIZE}
          total={filtered.length}
          onChange={setPage}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        loading={confirmLoading}
        onConfirm={handleConfirm}
        onClose={() => !confirmLoading && setConfirm(null)}
      />
    </Box>
  );
};

export default ProvidersPage;
